import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { cemeteryAPI, importAPI } from '../services/api';
import './ImportPage.css';
import Toast from '../components/Toast';
import CemeteryStatsGrid from '../components/CemeteryStatsGrid';
import CemeteryContextBar from '../components/CemeteryContextBar';

const IMPORT_TYPE_LABELS = {
  plots: 'Plots',
  plot_geojson: 'Plot GeoJSON',
  lot_geojson: 'Lot GeoJSON',
  section_geojson: 'Section GeoJSON',
  persons: 'Persons',
  interments: 'Interments',
  applications: 'Applications',
  stories: 'Stories',
  events: 'Events',
  notes: 'Notes',
};

// Friendly label for an uploaded file. Backend now returns split geojson types
// (plot_geojson/lot_geojson/section_geojson). The legacy 'geojson' bucket is
// kept as a fallback for files predating the split.
function friendlyTypeLabel(file) {
  const ft = (file.file_type || '').toLowerCase();
  const name = (file.original_filename || '').toLowerCase();

  if (IMPORT_TYPE_LABELS[ft]) return IMPORT_TYPE_LABELS[ft];

  if (ft === 'geojson') {
    if (/(^|[/_-])plots?([_.-]|$)/.test(name)) return 'Plot GeoJSON';
    if (/(^|[/_-])sections?([_.-]|$)/.test(name)) return 'Section GeoJSON';
    if (/(^|[/_-])lots?([_.-]|$)/.test(name)) return 'Lot GeoJSON';
    return 'GeoJSON';
  }

  if (!ft) return 'Unknown';
  return ft.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

function ImportPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [cemeteries, setCemeteries] = useState([]);
  const [selectedCemetery, setSelectedCemetery] = useState(searchParams.get('cemetery') || '');
  const [searchQuery, setSearchQuery] = useState('');
  const [files, setFiles] = useState([]);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [importTypes, setImportTypes] = useState({
    plots: true,
    plot_geojson: true,
    lot_geojson: true,
    section_geojson: true,
    persons: true,
    interments: true,
    applications: true,
    stories: true,
    events: true,
    notes: true,
  });
  const [selectedFiles, setSelectedFiles] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [taskId, setTaskId] = useState(null);
  const [importStatus, setImportStatus] = useState(null);
  const [statistics, setStatistics] = useState(null);
  const [wiping, setWiping] = useState(false);
  const [showDropFiles, setShowDropFiles] = useState(false);
  const pollFailuresRef = useRef(0);

  // Matches backend import_max_file_size_mb default (config.py)
  const MAX_FILE_SIZE_MB = 500;

  useEffect(() => {
    loadCemeteries();
  }, []);

  useEffect(() => {
    if (selectedCemetery) {
      loadUploadedFiles();
      loadStatistics();
      restoreActiveTask();
    }
  }, [selectedCemetery]);

  useEffect(() => {
    // Poll import status if task is running
    if (taskId) {
      const interval = setInterval(() => {
        checkImportStatus();
      }, 3000);
      return () => clearInterval(interval);
    }
  }, [taskId]);

  const loadCemeteries = async () => {
    try {
      const response = await cemeteryAPI.list();
      setCemeteries(response.data.cemeteries || response.data);
    } catch (err) {
      console.error('Failed to load cemeteries:', err);
    }
  };

  const filteredCemeteries = cemeteries.filter(cemetery =>
    cemetery.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    cemetery.unique_name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleSelectCemetery = (cemeteryUniqueName) => {
    setSelectedCemetery(cemeteryUniqueName);
    setSearchQuery('');
  };

  // Recover an in-progress import after page refresh — taskId only lives in
  // component state, so without this F5 silently loses progress tracking.
  const restoreActiveTask = async () => {
    try {
      const response = await importAPI.getActiveTasks();
      const active = (response.data.tasks || []).find(
        (t) => t.cemetery_unique_name === selectedCemetery && t.task_id
      );
      if (active) {
        setTaskId(active.task_id);
      }
    } catch (err) {
      console.error('Failed to check active import tasks:', err);
    }
  };

  const loadUploadedFiles = async () => {
    try {
      const response = await importAPI.listFiles(selectedCemetery);
      const files = Array.isArray(response.data) ? response.data : (response.data.files || []);
      setUploadedFiles(files);
      setSelectedFiles(new Set());

      if (files.length > 0) {
        const availableTypes = new Set(files.map(file => file.file_type));
        // Legacy uploads tagged every .geojson as 'geojson'; promote that bucket
        // to all 3 sub-types so old data still ticks the new boxes.
        const legacyGeojson = availableTypes.has('geojson');
        const newImportTypes = {
          plots: availableTypes.has('plots'),
          plot_geojson: availableTypes.has('plot_geojson') || legacyGeojson,
          lot_geojson: availableTypes.has('lot_geojson') || legacyGeojson,
          section_geojson: availableTypes.has('section_geojson') || legacyGeojson,
          persons: availableTypes.has('persons'),
          interments: availableTypes.has('interments'),
          applications: availableTypes.has('applications'),
          stories: availableTypes.has('stories'),
          events: availableTypes.has('events'),
          notes: availableTypes.has('notes'),
        };
        setImportTypes(newImportTypes);
      }
    } catch (err) {
      console.error('Failed to load uploaded files:', err);
    }
  };

  const loadStatistics = async ({ refresh = false } = {}) => {
    if (!selectedCemetery) return;

    try {
      const response = await importAPI.getStatistics(selectedCemetery, { refresh });
      setStatistics(response.data);
    } catch (err) {
      console.error('Failed to load statistics:', err);
      setStatistics(null);
    }
  };

  const handleWipeAllData = async () => {
    if (!selectedCemetery) return;

    const cemeteryName = cemeteries.find(c => c.unique_name === selectedCemetery)?.name || selectedCemetery;
    if (!window.confirm(
      `DANGER: This will permanently delete ALL data (plots, geojson, sections, lots, persons, interments, applications, stories, events, notes) from ${cemeteryName}. This action cannot be undone. Are you absolutely sure?`
    )) {
      return;
    }

    setWiping(true);
    setMessage(null);

    try {
      const allTypes = ['plots', 'geojson', 'sections', 'lots', 'persons', 'interments', 'applications', 'stories', 'events', 'notes'];
      const response = await importAPI.wipeData(selectedCemetery, allTypes);
      setMessage({
        type: 'success',
        text: `Successfully wiped ${response.data.total_deleted} records from ${cemeteryName}`,
      });
      loadStatistics();
    } catch (err) {
      setMessage({
        type: 'error',
        text: err.response?.data?.detail || 'Failed to wipe data',
      });
    } finally {
      setWiping(false);
    }
  };

  const checkImportStatus = async () => {
    if (!taskId || !selectedCemetery) return;

    try {
      const response = await importAPI.getStatus(selectedCemetery, taskId);
      pollFailuresRef.current = 0;
      setImportStatus(response.data);

      if (['COMPLETED', 'COMPLETED_WITH_WARNINGS', 'FAILED', 'CANCELLED'].includes(response.data.status)) {
        setTaskId(null);
        loadUploadedFiles();
        loadStatistics();
      }
    } catch (err) {
      console.error('Failed to check import status:', err);
      if (err.response?.status === 404) {
        setTaskId(null);
        setMessage({
          type: 'error',
          text: 'Import task no longer exists — it may have been cleaned up. Reload the page to see the latest state.',
        });
        return;
      }
      pollFailuresRef.current += 1;
      if (pollFailuresRef.current >= 5) {
        pollFailuresRef.current = 0;
        setTaskId(null);
        setMessage({
          type: 'error',
          text: 'Lost connection while tracking import progress. Reload the page to resume tracking.',
        });
      }
    }
  };

  const handleFileChange = (e) => {
    const newFiles = Array.from(e.target.files);
    const tooBig = newFiles.filter((f) => f.size > MAX_FILE_SIZE_MB * 1024 * 1024);
    if (tooBig.length > 0) {
      setMessage({
        type: 'error',
        text: `File(s) exceed the ${MAX_FILE_SIZE_MB}MB limit: ${tooBig.map((f) => f.name).join(', ')}`,
      });
      e.target.value = '';
      setFiles([]);
      return;
    }
    setFiles(newFiles);
  };

  const handleUpload = async () => {
    if (loading) return; // guard against double-click re-entry
    if (!selectedCemetery) {
      setMessage({ type: 'error', text: 'Please select a cemetery' });
      return;
    }

    if (files.length === 0) {
      setMessage({ type: 'error', text: 'Please select files to upload' });
      return;
    }

    setLoading(true);
    setMessage(null);

    try {
      const response = await importAPI.upload(selectedCemetery, files);
      const skipped = response.data.skipped_files || [];
      let text = `Successfully uploaded ${response.data.files_uploaded} file(s)`;
      let messageType = 'success';
      if (skipped.length > 0) {
        const lines = skipped.map((s) => {
          const name = typeof s === 'string' ? s : s.filename;
          const suggestion = typeof s === 'string' ? null : s.suggested_name;
          const reason = typeof s === 'string' ? '' : (s.reason || '');
          const suggestionPart = suggestion ? ` → rename to ${suggestion}` : '';
          return `• ${name}${suggestionPart}${reason ? ` (${reason})` : ''}`;
        });
        text += `\n${skipped.length} file(s) skipped:\n${lines.join('\n')}`;
        messageType = 'warning';
      }
      setMessage({ type: messageType, text });
      setFiles([]);
      document.getElementById('file-input').value = '';
      loadUploadedFiles();
    } catch (err) {
      setMessage({
        type: 'error',
        text: err.response?.data?.detail || 'Failed to upload files',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleStartImport = async () => {
    if (loading || taskId) return; // guard against double-click re-entry
    if (!selectedCemetery) {
      setMessage({ type: 'error', text: 'Please select a cemetery' });
      return;
    }

    const selectedTypes = Object.keys(importTypes).filter((key) => importTypes[key]);
    if (selectedTypes.length === 0) {
      setMessage({ type: 'error', text: 'Please select at least one import type' });
      return;
    }

    if (selectedDuplicateTypes.length > 0) {
      setMessage({
        type: 'error',
        text: `Duplicate files for type(s): ${selectedDuplicateTypes
          .map((t) => IMPORT_TYPE_LABELS[t] || t)
          .join(', ')}. Keep one file per type and delete the other(s) before starting the import.`,
      });
      return;
    }

    setLoading(true);
    setMessage(null);

    try {
      const response = await importAPI.start(selectedCemetery, selectedTypes);
      setTaskId(response.data.task_id);
      setMessage({
        type: 'success',
        text: `Import started successfully! Task ID: ${response.data.task_id}`,
      });
    } catch (err) {
      setMessage({
        type: 'error',
        text: err.response?.data?.detail || 'Failed to start import',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleImportTypeChange = (type) => {
    setImportTypes({
      ...importTypes,
      [type]: !importTypes[type],
    });
  };

  const handleCancelImport = async () => {
    if (!window.confirm('Are you sure you want to cancel this import?')) {
      return;
    }

    try {
      await importAPI.cancel(selectedCemetery, taskId);
      setMessage({ type: 'success', text: 'Import cancelled successfully' });
      setTaskId(null);
      setImportStatus(null);
      loadUploadedFiles();
    } catch (err) {
      setMessage({
        type: 'error',
        text: err.response?.data?.detail || 'Failed to cancel import',
      });
    }
  };

  const handleDeleteFile = async (fileId) => {
    if (!window.confirm('Are you sure you want to delete this file?')) {
      return;
    }

    try {
      await importAPI.deleteFile(selectedCemetery, fileId);
      setMessage({ type: 'success', text: 'File deleted successfully' });
      loadUploadedFiles();
    } catch (err) {
      setMessage({
        type: 'error',
        text: 'Failed to delete file',
      });
    }
  };

  const toggleFileSelection = (fileId) => {
    setSelectedFiles(prev => {
      const next = new Set(prev);
      if (next.has(fileId)) {
        next.delete(fileId);
      } else {
        next.add(fileId);
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedFiles.size === uploadedFiles.length) {
      setSelectedFiles(new Set());
    } else {
      setSelectedFiles(new Set(uploadedFiles.map(f => f.id)));
    }
  };

  const handleDeleteSelected = async () => {
    const count = selectedFiles.size;
    if (!window.confirm(`Are you sure you want to delete ${count} selected file(s)? This action cannot be undone.`)) {
      return;
    }

    setLoading(true);
    try {
      const results = await Promise.allSettled(
        [...selectedFiles].map(fileId => importAPI.deleteFile(selectedCemetery, fileId))
      );
      const succeeded = results.filter(r => r.status === 'fulfilled').length;
      const failed = results.filter(r => r.status === 'rejected').length;

      if (failed > 0) {
        setMessage({ type: 'warning', text: `Deleted ${succeeded} file(s), ${failed} failed` });
      } else {
        setMessage({ type: 'success', text: `Successfully deleted ${succeeded} file(s)` });
      }
      setSelectedFiles(new Set());
      loadUploadedFiles();
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to delete selected files' });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAllFiles = async () => {
    if (!window.confirm(
      `Are you sure you want to delete ALL ${uploadedFiles.length} uploaded file(s)? This action cannot be undone.`
    )) {
      return;
    }

    setLoading(true);
    try {
      const response = await importAPI.deleteAllFiles(selectedCemetery);
      setMessage({
        type: 'success',
        text: `Successfully deleted ${response.data.deleted_count} file(s)`,
      });
      loadUploadedFiles();
    } catch (err) {
      setMessage({
        type: 'error',
        text: err.response?.data?.detail || 'Failed to delete files',
      });
    } finally {
      setLoading(false);
    }
  };

  const downloadAsCSV = (items, type) => {
    const headers = ['Type', 'Message', 'File', 'Line', 'Field'];
    const rows = items.map(item => [
      item.error_type || item.code || type,
      `"${(item.message || '').replace(/"/g, '""')}"`,
      item.file || '',
      item.line_number || item.line || '',
      item.field || '',
    ]);
    const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const date = new Date().toISOString().split('T')[0];
    a.href = url;
    a.download = `import-${type}-${selectedCemetery}-${date}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Duplicate same-type files (legacy ambiguous 'geojson' exempt): import would
  // silently use only one of them, so the user must keep exactly one per type.
  const duplicateTypeGroups = Object.entries(
    uploadedFiles.reduce((acc, f) => {
      if (f.file_type === 'geojson') return acc;
      (acc[f.file_type] = acc[f.file_type] || []).push(f);
      return acc;
    }, {})
  ).filter(([, group]) => group.length > 1);
  const duplicateFileIds = new Set(
    duplicateTypeGroups.flatMap(([, group]) => group.map((f) => f.id))
  );
  const selectedDuplicateTypes = duplicateTypeGroups
    .map(([type]) => type)
    .filter((type) => importTypes[type]);

  return (
    <div className="container">
      <CemeteryContextBar
        cemeteryUniqueName={selectedCemetery}
        cemeteryName={cemeteries.find(c => c.unique_name === selectedCemetery)?.name}
        onChange={() => setSelectedCemetery('')}
      />
      <div className="page-header">
        <div className="page-header-text">
          <h1>Import Management</h1>
          <p>Upload and import cemetery data</p>
        </div>
      </div>

      <Toast message={message} onDismiss={() => setMessage(null)} />

      {/* Cemetery Selection */}
      <div className="card">
        <h2>Select Cemetery</h2>
        <div className="input-group">
          <label htmlFor="cemetery">Cemetery</label>
          <div className="search-container">
            <input
              type="text"
              id="cemetery"
              className="form-control"
              placeholder={selectedCemetery ?
                cemeteries.find(c => c.unique_name === selectedCemetery)?.name || selectedCemetery
                : "Search cemeteries..."}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              disabled={loading}
            />
            {searchQuery && filteredCemeteries.length > 0 && (
              <div className="search-results">
                {filteredCemeteries.map((cemetery) => (
                  <div
                    key={cemetery.unique_name}
                    className="search-result-item"
                    onClick={() => handleSelectCemetery(cemetery.unique_name)}
                  >
                    <strong>{cemetery.name}</strong>
                    <p className="text-muted">{cemetery.unique_name}</p>
                  </div>
                ))}
              </div>
            )}
            {searchQuery && filteredCemeteries.length === 0 && (
              <div className="search-results">
                <p className="text-muted">No cemeteries found</p>
              </div>
            )}
          </div>
          {selectedCemetery && (
            <div className="selected-item">
              <strong>Selected:</strong> {cemeteries.find(c => c.unique_name === selectedCemetery)?.name || selectedCemetery}
              <button
                className="btn btn-sm btn-secondary"
                onClick={() => setSelectedCemetery('')}
                disabled={loading}
              >
                Change
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Cemetery Data Statistics */}
      {selectedCemetery && (
        <CemeteryStatsGrid
          statistics={statistics || { cemetery_name: cemeteries.find(c => c.unique_name === selectedCemetery)?.name || selectedCemetery }}
          onRefresh={() => loadStatistics({ refresh: true })}
        >
          <button
            onClick={handleWipeAllData}
            className="btn btn-danger btn-sm"
            disabled={wiping || loading || !!taskId || !statistics}
          >
            {wiping ? 'Wiping...' : 'Wipe All Data'}
          </button>
        </CemeteryStatsGrid>
      )}

      {/* File Upload */}
      {selectedCemetery && (
        <>
          <div className="card">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <h2 style={{ margin: 0 }}>Upload Files</h2>
              <button
                type="button"
                onClick={() => setShowDropFiles((v) => !v)}
                className="btn btn-sm"
                aria-pressed={showDropFiles}
              >
                {showDropFiles ? 'Hide Drop Files' : 'Show Drop Files'}
              </button>
            </div>
            <p className="text-muted">
              Upload a ZIP file containing multiple CSV files, or upload individual CSV/GeoJSON files.
            </p>

            <div className="input-group">
              <label htmlFor="file-input">Select Files</label>
              <input
                id="file-input"
                type="file"
                multiple
                onChange={handleFileChange}
                accept=".zip,.csv,.geojson"
                disabled={loading}
              />
            </div>

            {files.length > 0 && (
              <div className="selected-files">
                <p>Selected files:</p>
                <ul>
                  {files.map((file, index) => (
                    <li key={index}>
                      {file.name} ({(file.size / 1024).toFixed(2)} KB)
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <button
              onClick={handleUpload}
              className="btn btn-primary"
              disabled={loading || files.length === 0}
            >
              {loading ? 'Uploading...' : 'Upload Files'}
            </button>
          </div>

          {/* Uploaded Files & Map Validation */}
          {uploadedFiles.length === 0 && showDropFiles && (
            <div className="card">
              <div className="empty-state">
                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
                </svg>
                <p className="empty-title">No files uploaded</p>
                <p className="empty-description">Upload ZIP, CSV, or GeoJSON files to begin</p>
              </div>
            </div>
          )}
          {uploadedFiles.length > 0 && (
            <div className="card uploaded-files-card">
              <div className="uploaded-banner">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
                <span>
                  <strong>{uploadedFiles.length} file{uploadedFiles.length === 1 ? '' : 's'} uploaded.</strong>
                  {' '}Review below, then start the import.
                </span>
              </div>
              {duplicateTypeGroups.length > 0 && (
                <div className="duplicate-banner">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
                  </svg>
                  <span>
                    <strong>Duplicate file types detected.</strong>{' '}
                    {duplicateTypeGroups.map(([type, group]) => (
                      `${IMPORT_TYPE_LABELS[type] || type}: ${group.map((f) => f.original_filename).join(', ')}`
                    )).join(' — ')}.
                    {' '}The import only uses one file per type — keep the correct file and delete the other(s) before starting.
                  </span>
                </div>
              )}
              <div className="card-header">
                <h2>Uploaded Files <span className="file-count">({uploadedFiles.length})</span></h2>
                <div style={{ display: 'flex', gap: '10px' }}>
                  {selectedFiles.size > 0 && (
                    <button
                      onClick={handleDeleteSelected}
                      className="btn btn-warning btn-sm"
                      disabled={loading}
                    >
                      Delete Selected ({selectedFiles.size})
                    </button>
                  )}
                  <button
                    onClick={handleDeleteAllFiles}
                    className="btn btn-danger btn-sm"
                    disabled={loading}
                  >
                    Delete All Files
                  </button>
                </div>
              </div>

              <table className="table uploaded-table">
                <thead>
                  <tr>
                    <th style={{ width: '40px' }}>
                      <input
                        type="checkbox"
                        checked={uploadedFiles.length > 0 && selectedFiles.size === uploadedFiles.length}
                        onChange={toggleSelectAll}
                      />
                    </th>
                    <th>Filename</th>
                    <th>Type</th>
                    <th>Size</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {uploadedFiles.map((file) => (
                    <tr key={file.id} className={duplicateFileIds.has(file.id) ? 'row-duplicate' : ''}>
                      <td>
                        <input
                          type="checkbox"
                          checked={selectedFiles.has(file.id)}
                          onChange={() => toggleFileSelection(file.id)}
                        />
                      </td>
                      <td className="cell-filename">
                        <div className="filename-friendly">
                          {friendlyTypeLabel(file)}
                          {duplicateFileIds.has(file.id) && (
                            <span className="badge badge-duplicate" title="Another file with the same import type exists — keep only one">DUPLICATE TYPE</span>
                          )}
                        </div>
                        <div className="filename-raw" title={file.original_filename}>
                          {file.original_filename}
                        </div>
                      </td>
                      <td><span className="cell-type">{friendlyTypeLabel(file)}</span></td>
                      <td>{file.file_size_mb.toFixed(2)} MB</td>
                      <td>
                        <span className={`badge badge-${(file.upload_status || '').toLowerCase() === 'uploaded' ? 'success' : 'warning'}`}>
                          {(file.upload_status || '').toUpperCase()}
                        </span>
                      </td>
                      <td>
                        <div className="cell-actions">
                          {/* Validate Map Button for GeoJSON */}
                          {file.file_type === 'geojson' && (
                            <button
                              className="btn btn-sm btn-primary"
                              onClick={() => navigate(`/import/preview?cemetery=${selectedCemetery}&file_id=${file.id}&filename=${file.original_filename}`)}
                              title="Validate Map Geometry"
                            >
                              Validate Map
                            </button>
                          )}

                          <button
                            className="btn-icon-delete"
                            onClick={() => handleDeleteFile(file.id)}
                            title="Delete file"
                          >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <polyline points="3 6 5 6 21 6"/><path d="M19 6l-2 14a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2"/>
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* Start Import */}
      {uploadedFiles.length > 0 && (
        <div className="card">
          <h2>Start Import</h2>

          <div className="import-types">
            <label>Import Types:</label>
            <div className="checkbox-grid">
              {Object.keys(importTypes).map((type) => (
                <label key={type} className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={importTypes[type]}
                    onChange={() => handleImportTypeChange(type)}
                    disabled={loading || taskId}
                  />
                  <span>{IMPORT_TYPE_LABELS[type] || (type.charAt(0).toUpperCase() + type.slice(1))}</span>
                </label>
              ))}
            </div>
          </div>

          <button
            onClick={handleStartImport}
            className="btn btn-success"
            disabled={loading || taskId || selectedDuplicateTypes.length > 0}
            title={selectedDuplicateTypes.length > 0
              ? `Duplicate files for: ${selectedDuplicateTypes.map((t) => IMPORT_TYPE_LABELS[t] || t).join(', ')} — delete the outdated file(s) first`
              : undefined}
          >
            {loading ? 'Processing...' : 'Start Import'}
          </button>
          {selectedDuplicateTypes.length > 0 && (
            <p className="duplicate-hint">
              Import blocked: duplicate files for {selectedDuplicateTypes.map((t) => IMPORT_TYPE_LABELS[t] || t).join(', ')}. Keep one file per type and delete the other(s) above.
            </p>
          )}
        </div>
      )}

      {/* Import Status */}
      {importStatus && (
        <div className="card">
          <div className="card-header">
            <h2>Import Status</h2>
            {taskId && ['PROCESSING', 'VALIDATING'].includes(importStatus.status) && (
              <button
                onClick={handleCancelImport}
                className="btn btn-danger btn-sm"
              >
                Cancel Import
              </button>
            )}
          </div>
          <div className="import-status">
            <div className="status-row">
              <span className="status-label">Status:</span>
              <span className={`status-value status-${importStatus.status.toLowerCase()}`}>
                {importStatus.status}
              </span>
            </div>
            <div className="status-row">
              <span className="status-label">Progress:</span>
              <span className="status-value">{importStatus.progress}%</span>
            </div>
            {importStatus.current_operation && (
              <div className="status-row">
                <span className="status-label">Current Operation:</span>
                <span className="status-value">{importStatus.current_operation}</span>
              </div>
            )}
            <div className="status-row">
              <span className="status-label">Total Records:</span>
              <span className="status-value">{importStatus.total_records}</span>
            </div>
            <div className="status-row">
              <span className="status-label">Processed:</span>
              <span className="status-value">{importStatus.processed_records}</span>
            </div>
            <div className="status-row">
              <span className="status-label">Successful:</span>
              <span className="status-value status-success">
                {importStatus.successful_records}
              </span>
            </div>
            <div className="status-row">
              <span className="status-label">Failed:</span>
              <span className="status-value status-failed">
                {importStatus.failed_records}
              </span>
            </div>

            {importStatus.progress > 0 && importStatus.progress < 100 && (
              <div className="progress-bar">
                <div
                  className="progress-fill"
                  style={{ width: `${importStatus.progress}%` }}
                ></div>
              </div>
            )}

            {/* Warning Summary (COMPLETED_WITH_WARNINGS) */}
            {importStatus.status === 'COMPLETED_WITH_WARNINGS' && importStatus.error_summary && (
              <div className="warning-summary" style={{
                marginTop: '20px',
                padding: '15px',
                backgroundColor: '#fff8e1',
                border: '1px solid #f9a825',
                borderRadius: '4px',
              }}>
                <h3 style={{ color: '#f57f17', marginBottom: '8px', marginTop: 0 }}>Completed with Warnings</h3>
                <p style={{ margin: 0, color: '#333' }}>{importStatus.error_summary}</p>
              </div>
            )}

            {/* Warnings List */}
            {importStatus.warnings && importStatus.warnings.length > 0 && (
              <div className="warning-details" style={{ marginTop: '20px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                  <h3 style={{ color: '#f57f17', margin: 0 }}>
                    Warnings ({importStatus.warnings.length})
                  </h3>
                  <button
                    className="btn btn-sm btn-outline-warning"
                    onClick={() => downloadAsCSV(importStatus.warnings, 'warnings')}
                    style={{ color: '#f57f17', borderColor: '#f57f17' }}
                  >
                    Download CSV
                  </button>
                </div>
                <div className="warning-list" style={{
                  maxHeight: '300px',
                  overflowY: 'auto',
                  border: '1px solid #f9a825',
                  borderRadius: '4px',
                  padding: '10px',
                  backgroundColor: '#fff8e1'
                }}>
                  {importStatus.warnings.map((warning, index) => (
                    <div key={index} style={{
                      padding: '8px',
                      marginBottom: '8px',
                      backgroundColor: 'white',
                      borderLeft: '4px solid #f9a825',
                      borderRadius: '4px'
                    }}>
                      <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                        {warning.code || 'Warning'}
                      </div>
                      <div style={{ color: '#666' }}>{warning.message}</div>
                      {warning.file && (
                        <div style={{ fontSize: '0.9em', color: '#999', marginTop: '4px' }}>
                          File: <code>{warning.file}</code>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Error Summary */}
            {importStatus.status === 'FAILED' && importStatus.error_summary && (
              <div className="error-summary" style={{
                marginTop: '20px',
                padding: '15px',
                backgroundColor: '#ffebee',
                border: '1px solid #d32f2f',
                borderRadius: '4px',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <div>
                    <h3 style={{ color: '#d32f2f', marginBottom: '8px', marginTop: 0 }}>Import Failed</h3>
                    <p style={{ margin: 0, color: '#333' }}>{importStatus.error_summary}</p>
                  </div>
                  {importStatus.errors && importStatus.errors.length > 0 && (
                    <button
                      className="btn btn-sm btn-outline-danger"
                      onClick={() => downloadAsCSV(importStatus.errors, 'errors')}
                      style={{ whiteSpace: 'nowrap', marginLeft: '15px' }}
                    >
                      Download Errors CSV
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Error Details */}
            {importStatus.errors && importStatus.errors.length > 0 && (
              <div className="error-details" style={{ marginTop: '20px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                  <h3 style={{ color: '#d32f2f', margin: 0 }}>
                    Error Details ({importStatus.errors.length} errors)
                  </h3>
                  <button
                    className="btn btn-sm btn-outline-danger"
                    onClick={() => downloadAsCSV(importStatus.errors, 'errors')}
                  >
                    Download CSV
                  </button>
                </div>
                <div className="error-list" style={{
                  maxHeight: '400px',
                  overflowY: 'auto',
                  border: '1px solid #d32f2f',
                  borderRadius: '4px',
                  padding: '10px',
                  backgroundColor: '#ffebee'
                }}>
                  {importStatus.errors.map((error, index) => (
                    <div key={index} className="error-item" style={{
                      padding: '10px',
                      marginBottom: '10px',
                      backgroundColor: 'white',
                      borderLeft: '4px solid #d32f2f',
                      borderRadius: '4px'
                    }}>
                      <div style={{ fontWeight: 'bold', marginBottom: '5px' }}>
                        {error.error_type || error.code || 'Error'}
                        {error.line_number && ` (Line ${error.line_number})`}
                        {error.line && !error.line_number && ` (Line ${error.line})`}
                      </div>
                      <div style={{ color: '#666', marginBottom: '5px' }}>
                        {error.message}
                      </div>
                      {error.field && (
                        <div style={{ fontSize: '0.9em', color: '#999' }}>
                          Field: <code>{error.field}</code>
                        </div>
                      )}
                      {error.record_snippet && (
                        <details style={{ marginTop: '5px', fontSize: '0.85em' }}>
                          <summary style={{ cursor: 'pointer', color: '#666' }}>
                            View record snippet
                          </summary>
                          <pre style={{
                            marginTop: '5px',
                            padding: '5px',
                            backgroundColor: '#f5f5f5',
                            borderRadius: '4px',
                            overflow: 'auto'
                          }}>
                            {JSON.stringify(error.record_snippet, null, 2)}
                          </pre>
                        </details>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default ImportPage;
